import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Visibility map for the current user's role: { tabKey: bool }
  // Missing keys default to true on the client — no nav flicker on load.
  const [tabVisibility, setTabVisibility] = useState({});

  // Fetches tab visibility for the current session token. Fire-and-forget —
  // a failure leaves tabVisibility as {} which defaults every tab to visible.
  const fetchTabVisibility = useCallback((token) => {
    fetch(`${API_BASE}/api/tab-visibility`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : {})
      .then(setTabVisibility)
      .catch(() => {});
  }, []);

  // Refreshes tab visibility on demand (called by Config UI after saving).
  const refreshTabVisibility = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (token) fetchTabVisibility(token);
  }, [fetchTabVisibility]);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        fetchTabVisibility(token);
      } else {
        localStorage.removeItem('authToken');
        setCurrentUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('authToken');
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  }, [fetchTabVisibility]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    localStorage.setItem('authToken', data.token);
    setCurrentUser(data.user);
    fetchTabVisibility(data.token);
    return data.user;
  };

  const logout = async () => {
    const token = localStorage.getItem('authToken');

    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    localStorage.removeItem('authToken');
    setCurrentUser(null);
    setTabVisibility({});
  };

  const hasPermission = (permissionKey) => {
    if (!currentUser) return false;
    // Admin has all permissions
    if (currentUser.role === 'admin') return true;
    // Manager has OBD-related permissions
    if (currentUser.role === 'manager') {
      return ['obdSignOn', 'obdDashboard', 'obdManager'].includes(permissionKey);
    }
    // Associate only has basic obdSignOn permission (worker view)
    if (currentUser.role === 'associate') {
      return permissionKey === 'obdSignOn';
    }
    // Report role only has KPI access
    if (currentUser.role === 'report') {
      return permissionKey === 'kpi';
    }
    return false;
  };

  // Role helper properties
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const isAssociate = currentUser?.role === 'associate';
  const isReport = currentUser?.role === 'report';

  // Permission helpers for navigation/routes
  const canAccessManagerFeatures = isManager || isAdmin; // OBD Sign-On Manager View + OBD Dashboard
  const canAccessAllFeatures = isAdmin; // All tabs
  const canAccessKPI = isAdmin || isReport; // KPI tab access
  const canAccessKPIDashboard = isAdmin || isManager || isReport; // KPI Dashboard access (manager is view-only)

  // Tab visibility helpers
  const isTabVisible = (tabKey) => tabVisibility[tabKey] !== false;

  const value = {
    currentUser,
    loading,
    login,
    logout,
    hasPermission,
    isAuthenticated: !!currentUser,
    // Individual role checks
    isAdmin,
    isManager,
    isAssociate,
    isReport,
    // Permission helpers
    canAccessManagerFeatures,
    canAccessAllFeatures,
    canAccessKPI,
    canAccessKPIDashboard,
    // Tab visibility (cosmetic layer on top of permission floor)
    tabVisibility,
    isTabVisible,
    refreshTabVisibility,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;